<script setup lang="ts">
import { reactive, ref } from 'vue'
import { useRouter } from 'vue-router'
import { estimatesApi } from '@/api/entities/review/estimates'

definePage({
  meta: {
    title: '견적서 작성',
    action: 'read',
    subject: 'Admin',
  },
})

const router = useRouter()

// 폼 데이터
const formData = reactive({
  company_name: '',
  business_number: '',
})

// 상태
const saving = ref(false)
const snackbar = ref(false)
const snackbarMessage = ref('')
const snackbarColor = ref('success')

// 확인 다이얼로그
const confirmDialog = ref(false)

// 폼 유효성
const formValid = ref(false)

// 유효성 검사 규칙
const rules = {
  required: (v: string) => !!v || '필수 입력 항목입니다.',
  businessNumber: (v: string) => {
    if (!v)
      return '필수 입력 항목입니다.'
    const regex = /^\d{3}-\d{2}-\d{5}$/

    return regex.test(v) || 'XXX-XX-XXXXX 형식으로 입력해주세요.'
  },
}

// 알림 표시
const showNotification = (message: string, color: string = 'success') => {
  snackbarMessage.value = message
  snackbarColor.value = color
  snackbar.value = true
}

// 등록 확인 다이얼로그 열기
const openConfirmDialog = () => {
  if (!formValid.value) {
    showNotification('필수 입력 항목을 확인해주세요.', 'warning')

    return
  }
  confirmDialog.value = true
}

// 견적서 등록
const createEstimate = async () => {
  confirmDialog.value = false
  saving.value = true

  try {
    const { success, error } = await estimatesApi.createEstimate(formData)

    if (success) {
      showNotification('견적서가 등록되었습니다.')
      setTimeout(() => {
        router.push({ name: 'review-estimates' })
      }, 1000)
    }
    else {
      const errorMsg = error?.response?.data?.message || '등록에 실패했습니다.'

      showNotification(errorMsg, 'error')
    }
  }
  catch (error: any) {
    showNotification(error?.message || '등록에 실패했습니다.', 'error')
  }
  finally {
    saving.value = false
  }
}

// 취소
const goBack = () => {
  router.push({ name: 'review-estimates' })
}
</script>

<template>
  <VCard>
    <VCardTitle>견적서 작성</VCardTitle>
    <VCardText>
      <VForm
        v-model="formValid"
        @submit.prevent="openConfirmDialog"
      >
        <VRow>
          <VCol
            cols="12"
            md="6"
          >
            <VTextField
              v-model="formData.company_name"
              label="업체명"
              placeholder="업체명을 입력하세요"
              :rules="[rules.required]"
              required
            />
          </VCol>
        </VRow>

        <VRow>
          <VCol
            cols="12"
            md="6"
          >
            <VTextField
              v-model="formData.business_number"
              label="사업자 등록번호"
              placeholder="XXX-XX-XXXXX 형식으로 입력해주세요"
              :rules="[rules.businessNumber]"
              required
            />
          </VCol>
        </VRow>

        <VRow class="mt-4">
          <VCol>
            <VBtn
              variant="outlined"
              class="me-2"
              @click="goBack"
            >
              취소
            </VBtn>
            <VBtn
              color="primary"
              type="submit"
              :loading="saving"
            >
              견적서 등록
            </VBtn>
          </VCol>
        </VRow>
      </VForm>
    </VCardText>

    <!-- 확인 다이얼로그 -->
    <VDialog
      v-model="confirmDialog"
      max-width="400"
    >
      <VCard>
        <VCardTitle>견적서 등록</VCardTitle>
        <VCardText>
          견적서를 등록하시겠습니까? 등록된 견적서는 수정이 불가능합니다.
        </VCardText>
        <VCardActions>
          <VSpacer />
          <VBtn
            variant="text"
            @click="confirmDialog = false"
          >
            취소
          </VBtn>
          <VBtn
            color="primary"
            :loading="saving"
            @click="createEstimate"
          >
            등록
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>

    <!-- 스낵바 -->
    <VSnackbar
      v-model="snackbar"
      :color="snackbarColor"
      :timeout="3000"
    >
      {{ snackbarMessage }}
    </VSnackbar>
  </VCard>
</template>
